import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DynamicDataService } from '../Services/dynamic-data.service';

/**
 * App-scope tab-title strategy. Runs on every successful navigation regardless
 * of which components are mounted, so header-less routes (login, callback,
 * variance PDF) get a correct title and there's no stuck "Spendkey" default
 * on first render.
 *
 * Prior implementation lived inside HeaderComponent's router.events
 * subscription — that raced HeaderComponent's own lifecycle (Router.events is a
 * plain Subject, no replay) so the very first NavigationEnd was missed on
 * routes where the header mounted mid-navigation (post-login redirect being
 * the worst case). The static index.html fallback then leaked visibly.
 *
 * The label is the sidebar entry that owns the URL's first segment, so every
 * sub-page (savings-tracker/initiative/:id, admin-console/audit/log-viewer)
 * shows its parent page's name, translated the same way the sidebar renders
 * it. Pages with no sidebar entry (login, forgot-password), or a first load
 * before the sidebar cache is written, fall back to the slug: first URL
 * segment, max 25 chars, special-case labels, title-cased. Formatted as
 * "<Label> - Spendkey".
 */
@Injectable({ providedIn: 'root' })
export class SpendkeyTitleStrategy extends TitleStrategy {
  private readonly titleService = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly dynamicDataService = inject(DynamicDataService);

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const routeTitle = this.buildTitle(snapshot);
    if (routeTitle !== undefined) {
      this.titleService.setTitle(routeTitle);
      return;
    }
    const segment = SpendkeyTitleStrategy.firstSegment(snapshot.url);
    const label = this.sidebarLabel(segment) ?? this.deriveLabel(segment);
    this.titleService.setTitle(`${label} - Spendkey`);
  }

  /** `/savings-tracker/initiative/12?tab=x` → `savings-tracker`. */
  private static firstSegment(url: string | null | undefined): string {
    return (url ?? '').trim().replace(/^\/+/, '').split(/[?#]/)[0].split('/')[0];
  }

  private sidebarLabel(segment: string): string | undefined {
    if (!segment) return undefined;
    const rows = this.dynamicDataService.getDataFromLocal('SideBar');
    if (!Array.isArray(rows)) return undefined;
    const key = segment.toLowerCase();
    const row = rows.find((r: any) =>
      r?.Title && SpendkeyTitleStrategy.firstSegment(r.Link).toLowerCase() === key);
    return row ? this.translate.instant(row.Title) : undefined;
  }

  private deriveLabel(segment: string): string {
    // substr(1, 25) quirk kept from the header impl: cap the slug at 25 chars.
    let slug = segment.substring(0, 25).replaceAll('-', ' ');

    // Client-specific overrides applied first (juliusbaer renames contract-*
    // routes to agreement-*). Kept verbatim from the header impl to preserve
    // per-tenant labels.
    if (typeof window !== 'undefined' && window.location.host === 'juliusbaer.spendkey.app') {
      if (slug === 'contracts') slug = 'Agreements';
      else if (slug === 'view contract') slug = 'View agreement';
      else if (slug === 'add new contract') slug = 'Add new agreement';
      else if (slug === 'edit contract') slug = 'Edit agreement';
      else if (slug === 'edit highlight contract') slug = 'Edit agreement';
      else if (slug === 'find in contract') slug = 'Find in agreement';
    }

    // Cross-tenant slug → label overrides.
    if (slug === 'taxonomy') slug = 'Classify Spend Data';
    else if (slug === 'edit highlight contract') slug = 'Edit agreement';
    else if (slug === 'ProductServices') slug = 'Product & Service ';
    else if (slug === 'keyManagement') slug = 'key Management';

    // Title-case first char, lowercase the rest (matches the old titleDoc helper).
    return slug.charAt(0).toUpperCase() + slug.slice(1).toLowerCase();
  }
}
